import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { negate } from 'lodash-es';
import { computed } from 'vue';

import { useAdAccountConnectionStatus } from '@/app/dashboards/composables/useAdAccountConnectionStatus';
import { useReport } from '@/app/dashboards/composables/useReport';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';
import { useReportType } from '@/app/dashboards/composables/useReportType';
import {
  ACCOUNT_LEVEL_REPORT_SERVER_ERROR_TYPES,
  COMPETITIVE_AVERAGE_KEY,
  DASHBOARD_TOOLTIPS,
  FETCH_REPORT_SERVER_ERROR_TYPES,
  NO_ACCESS_MESSAGES,
  REPORT_ERROR_MESSAGES,
} from '@/app/dashboards/constants';
import { METRIC_PREFIX } from '@/app/dashboards/reportConstants';
import { getAdsPlatformController } from '@/app/dashboards/utils/getAdsPlatformController';
import { parseCreatorId } from '@/app/dashboards/utils/report.utils';
import { isContentReport, REPORTS } from '@/app/dashboards/utils/reports.enum';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { toolTips } from '@/config';
import { BRAND } from '@/models/auth/permissions.enum';
import {
  AGGREGATE_REPORT_TYPES,
  LISTENING_REPORT_AGGREGATE_BY_TYPES,
} from '@/models/dashboards/aggregate-type.enum';
import {
  ALL_CHANNELS_KEY,
  CHANNELS,
  getAccountDisconnectionMessage,
  TSI_ENABLED_CHANNELS,
} from '@/models/dashboards/channels.enum';
import { metricIsOrganicAndPromoted } from '@/models/dashboards/metrics';
import { useContentTagsStore } from '@/stores/content-tags';
import { useDashboardPermissionsStore } from '@/stores/dashboard-permissions';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useMetricsStore } from '@/stores/metrics';
import { usePlatformStore } from '@/stores/platform';

export function useReportErrors({ report, loading, serverErrorMessage } = {}) {
  const platformStore = usePlatformStore();
  const identityStore = useIdentityStore();
  const flagStore = useFlagStore();
  const dashboardPermissionsStore = useDashboardPermissionsStore();
  const metricsStore = useMetricsStore();
  const { customMetricNotFound, getCustomReportDetailsFromId } = useCustomMetrics();
  const { allOrganizationsContentTags } = useContentTagsStore();
  const {
    reportType,
    reportChannels,
    reportMetric,
    reportBrands,
    reportBrandIds,
    reportCreators,
    reportMetricDetails,
    firstChannel,
    firstMetric,
    metricTypeReportKey,
    isCustomMetricReportTile,
    reportData,
    contentTagIds,
    reportCompetitorsMetrics,
    reportCreatorMetrics,
    reportCategory,
    reportError,
    secondaryAggregateBy,
    aggregateBy,
    isTimeSeriesReport,
    metrics,
  } = useReport({ report });

  const { isCompetitiveReport, isBenchmarkReport, isAdsReportType, isCreatorReport } =
    useReportType({ reportType });

  const { isCommunityReportCategory, isListeningReportCategory } = useReportCategory({
    reportCategory,
  });

  const { getConnectionUrl, getAdPlatformForChannel, AD_PLATFORM_CONFIG } =
    useAdAccountConnectionStatus({ brands: reportBrandIds });

  const getChannelIconName = (channel) => {
    const channelIconMap = {
      INSTAGRAM: 'instagram-color',
      FACEBOOK: 'facebook-color',
      TIKTOK: 'tiktok-color',
      PINTEREST: 'pinterest-color',
      INSTAGRAM_STORIES: 'instagram-story-color',
      INSTAGRAM_STORY: 'instagram-story-color',
      TWITTER: 'twitter-color',
      X: 'x-color',
      YOUTUBE: 'youtube-color',
      LINKEDIN: 'linkedin-color',
      INSTAGRAM_UGC: 'instagram-color',
      TIKTOK_UGC: 'tiktok-color',
      INSTAGRAM_STORIES_UGC: 'instagram-story-color',
    };
    return channelIconMap[channel] || `${channel.toLowerCase()}-color`;
  };

  function brandsWithoutAdsPermission(brand) {
    const channel = reportChannels.value?.[0];
    return (
      !dashboardPermissionsStore.brandCanAccessAds(brand, channel) ||
      !dashboardPermissionsStore.userCanAccessAds(brand, channel)
    );
  }

  function canUserAccessCompetitors(brand) {
    if (!brand?.label || !identityStore.identity?.permissions?.competitive?.[brand.label])
      return false;
    return identityStore.identity.permissions.competitive[brand.label].can_access_competitors;
  }

  function userOrBrandCanAccessCompetitor(brand) {
    if (!brand) return false;

    return (
      identityStore.guard(BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS, brand) ||
      canUserAccessCompetitors(brand)
    );
  }

  function brandCanAccessDashboards(brand) {
    return identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_DASHBOARDS, brand);
  }

  function brandCanAccessBenchmark(brand) {
    return identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS, brand);
  }

  function brandCanAccessCommunity(brand) {
    return identityStore.guard(BRAND.COMMUNITY.CAN_ACCESS_COMMUNITY, brand);
  }

  function brandCanAccessAds(brand, channel) {
    const adsPlatformController = getAdsPlatformController(channel);
    return adsPlatformController?.brandHasPermission(brand) ?? false;
  }

  function userCanAccessAds(brand, channel) {
    const adsPlatformController = getAdsPlatformController(channel);
    return adsPlatformController?.userHasPermission(brand) ?? false;
  }

  function canAccessAdsForBrand(brand, channel) {
    return brandCanAccessAds(brand, channel) && userCanAccessAds(brand, channel);
  }

  const canAccessIndustryBenchmarks = computed(() => {
    if (!identityStore.allUserBrands?.length) return false;

    return identityStore.allUserBrands.some((brand) =>
      identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS, brand),
    );
  });

  function canAccessCreator(creator) {
    return creator.user_has_access;
  }

  function canAccessCompetitor(competitor) {
    if (!competitor?.isCompetitor || competitor?.id === COMPETITIVE_AVERAGE_KEY) {
      return true;
    }

    const brandDetails = competitor.brandDetails ?? [];
    return !!brandDetails.some(
      (brand) =>
        canUserAccessCompetitors(identityStore.identityBrandsById[brand.brandId]) ||
        identityStore.guard(
          BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS,
          identityStore.identityBrandsById[brand.brandId],
        ),
    );
  }

  function canAccessMetrics(brand) {
    return brand?.backend_permissions?.brand?.can_access_metric;
  }

  const brandsUserCannotAccess = computed(() =>
    reportBrands.value?.reduce((acc, brand) => {
      if (isAdsReportType.value) {
        acc[brand.id] = brandsWithoutAdsPermission(brand);
      } else {
        acc[brand.id] = !brand.backend_permissions?.user?.can_access_brand;
      }
      return acc;
    }, {}),
  );

  const disconnectedChannels = computed(() => {
    if (loading.value || !reportChannels.value || !reportBrands.value) return {};

    return reportBrands.value.reduce((acc, brand) => {
      const brandId = brand.id;
      if (brand.isCompetitor) return acc;

      const disconnected = (
        reportChannels.value?.[0] === CHANNELS.TSI_CHANNELS.value
          ? TSI_ENABLED_CHANNELS
          : reportChannels.value
      ).filter((channel) => !platformStore.hasCompletePlatformConnection(brandId, channel));

      if (disconnected.length > 0) {
        acc[brandId] = disconnected;
      }

      return acc;
    }, {});
  });

  const brandsWithoutContentTagAccess = computed(() => {
    if (!contentTagIds.value?.length || !reportBrands.value) return {};

    return reportBrands.value.reduce((acc, brand) => {
      if (brand.isCompetitor) return acc;

      const inaccessibleTags = contentTagIds.value.reduce((tags, tagId) => {
        const contentTag = allOrganizationsContentTags.find((tag) => tag.id === tagId);
        if (!contentTag || contentTag.brandIds?.includes(brand.id)) {
          return tags;
        }
        return [...tags, contentTag.name];
      }, []);

      if (inaccessibleTags.length > 0) {
        acc[brand.id] = inaccessibleTags;
      }

      return acc;
    }, {});
  });

  const brandsWithoutCustomMetricAccess = computed(() => {
    if (!isCustomMetricReportTile.value || !reportBrands.value || !firstMetric.value) return {};
    const customMetric = getCustomReportDetailsFromId(firstMetric.value);

    return reportBrands.value.reduce((acc, brand) => {
      if (brand.isCompetitor) return acc;

      if (!customMetric?.brandIds.includes(brand.id)) {
        acc[brand.id] = true;
      }

      return acc;
    }, {});
  });

  const brandsWithoutCompetitiveAccess = computed(() => {
    if (!isCompetitiveReport.value || !reportBrands.value) return {};

    return reportBrands.value.reduce((acc, brand) => {
      if (brand.isCompetitor) return acc;

      if (!userOrBrandCanAccessCompetitor(brand)) {
        acc[brand.id] = true;
      }

      return acc;
    }, {});
  });

  const createAccessMap = (condition, items, checkFn, mapFn = null) => {
    if (!condition) return {};

    return items?.reduce((acc, item) => {
      if (item.isCompetitor && mapFn === null) return acc;

      const result = checkFn(item);
      if (result) {
        acc[item.id] = mapFn ? result : true;
      }
      return acc;
    }, {});
  };

  const brandsWithoutAdsAccess = computed(() =>
    createAccessMap(
      isAdsReportType.value && reportChannels.value && reportBrands.value,
      reportBrands.value,
      (brand) => {
        if (brand.isCompetitor) return null;
        const channelsWithoutAccess = reportChannels.value.filter(
          (channel) => !canAccessAdsForBrand(brand, channel),
        );
        return channelsWithoutAccess.length > 0 ? channelsWithoutAccess : null;
      },
      (channels) => channels,
    ),
  );

  const brandsWithoutDashboardsAccess = computed(() =>
    createAccessMap(
      reportBrands.value?.length,
      reportBrands.value,
      (brand) => !brandCanAccessDashboards(brand),
    ),
  );

  const brandsWithoutBenchmarkAccess = computed(() =>
    createAccessMap(
      isBenchmarkReport.value && reportBrands.value,
      reportBrands.value,
      (brand) => !brand.isCompetitor && !brandCanAccessBenchmark(brand),
    ),
  );

  const brandsWithoutCommunityAccess = computed(() =>
    createAccessMap(
      isCommunityReportCategory.value && reportBrands.value,
      reportBrands.value,
      (brand) => !brandCanAccessCommunity(brand),
    ),
  );

  const competitorsWithoutAccess = computed(() =>
    createAccessMap(
      isCompetitiveReport.value && reportCompetitorsMetrics.value,
      reportCompetitorsMetrics.value,
      (competitor) => !canAccessCompetitor(competitor),
    ),
  );

  const creatorsWithoutAccess = computed(() =>
    createAccessMap(
      isCreatorReport.value && reportCreatorMetrics.value,
      reportCreatorMetrics.value,
      (creator) => !canAccessCreator(creator),
    ),
  );

  const hasChannelErrors = computed(() => {
    if (!disconnectedChannels.value) return false;

    return Object.keys(disconnectedChannels.value).length > 0;
  });

  const hasCustomMetricErrors = computed(() => {
    if (!isCustomMetricReportTile.value || !brandsWithoutCustomMetricAccess.value) return false;
    return Object.keys(brandsWithoutCustomMetricAccess.value).length > 0;
  });

  const hasContentTagErrors = computed(() => {
    if (!brandsWithoutContentTagAccess.value) return false;

    return Object.keys(brandsWithoutContentTagAccess.value).length > 0;
  });

  const hasCompetitiveAccessErrors = computed(() => {
    if (!brandsWithoutCompetitiveAccess.value) return false;

    return Object.keys(brandsWithoutCompetitiveAccess.value).length > 0;
  });

  const hasAdsAccessErrors = computed(() => {
    if (!brandsWithoutAdsAccess.value) return false;

    return Object.keys(brandsWithoutAdsAccess.value).length > 0;
  });

  const hasBenchmarkAccessErrors = computed(() => {
    if (!brandsWithoutBenchmarkAccess.value) return false;

    return Object.keys(brandsWithoutBenchmarkAccess.value).length > 0;
  });

  const hasCommunityAccessErrors = computed(() => {
    if (!brandsWithoutCommunityAccess.value) return false;

    return Object.keys(brandsWithoutCommunityAccess.value).length > 0;
  });

  const externalApiError = computed(
    () => reportError.value?.type === FETCH_REPORT_SERVER_ERROR_TYPES.EXTERNAL_DASH_API,
  );

  const noAccessToTopics = computed(
    () => reportError.value?.type === FETCH_REPORT_SERVER_ERROR_TYPES.NO_ACCESS_TO_TOPICS,
  );

  const crossOrgTopics = computed(
    () => reportError.value?.type === FETCH_REPORT_SERVER_ERROR_TYPES.CROSS_ORG_TOPICS,
  );

  const invalidTimeScaleForNonAdditiveMetric = computed(
    () =>
      reportError.value?.type ===
      FETCH_REPORT_SERVER_ERROR_TYPES.INVALID_TIMESCALE_NON_ADDITIVE_METRIC,
  );

  const hasCompetitorAccessErrors = computed(() => {
    if (!competitorsWithoutAccess.value) return false;
    return Object.keys(competitorsWithoutAccess.value).length > 0;
  });

  const disconnectedChannel = computed(() => {
    if (!reportChannels.value?.length || !reportBrands.value) return null;

    return (
      reportChannels.value.find((channel) =>
        reportBrands.value.some(
          (brand) => !platformStore.hasCompletePlatformConnection(brand.id, channel),
        ),
      ) || null
    );
  });

  const allBrandsHaveChannelsDisconnected = computed(() => {
    if (!reportBrands.value || !reportChannels.value) return false;

    const ownedBrands = reportBrands.value.filter((brand) => !brand.isCompetitor);
    if (ownedBrands.length === 0) return false;

    return ownedBrands.every((brand) =>
      reportChannels.value.every(
        (channel) => !platformStore.hasCompletePlatformConnection(brand.id, channel),
      ),
    );
  });

  const isCustomMetricDeleted = computed(() => {
    const metric = firstMetric.value;
    if (!metric) return false;

    const isNotCustomMetric = !metric.startsWith(METRIC_PREFIX.CUSTOM);
    if (isNotCustomMetric) return false;

    return customMetricNotFound(metric);
  });

  const reportRequiresAdAccountButDisconnected = computed(
    () => reportError.value?.type === FETCH_REPORT_SERVER_ERROR_TYPES.MISSING_AD_ACCOUNT,
  );

  const reportIsOrganicAndPromotedWithDisconnectedAdAccount = computed(() => {
    if (!reportRequiresAdAccountButDisconnected.value) return false;
    const metricDetails =
      metricsStore.metricDetails?.[firstChannel.value]?.[metricTypeReportKey.value]?.[
        reportMetric.value
      ];
    return metricIsOrganicAndPromoted(metricDetails);
  });

  const adAccountDisconnectedPlatform = computed(() => {
    if (!reportRequiresAdAccountButDisconnected.value) return null;
    return getAdPlatformForChannel(firstChannel.value);
  });

  const brandsWithoutRequiredAdAccount = computed(() => {
    if (
      !reportMetric.value ||
      !firstChannel.value ||
      !metricTypeReportKey.value ||
      !reportBrands.value?.length
    ) {
      return {};
    }
    return reportBrands.value.reduce((acc, brand) => {
      if (!brand?.errors) return acc;
      const isMissingAdAccount = brand.errors.includes(
        ACCOUNT_LEVEL_REPORT_SERVER_ERROR_TYPES.MISSING_AD_ACCOUNT,
      );
      const isMissingConnection = brand.errors.includes(
        ACCOUNT_LEVEL_REPORT_SERVER_ERROR_TYPES.MISSING_AD_ACCOUNT_CONNECTION,
      );
      if (isMissingAdAccount || isMissingConnection) {
        const adPlatformConfig = AD_PLATFORM_CONFIG.find((config) =>
          config.channels.includes(firstChannel.value),
        );
        if (!adPlatformConfig) return acc;

        acc[brand.id] = {
          platform: adPlatformConfig.name,
          channel: firstChannel.value,
          partiallyConnected:
            isMissingAdAccount && !isMissingConnection && adPlatformConfig.name === 'Meta',
        };
      }
      return acc;
    }, {});
  });

  const contentReportHasNoData = computed(() => {
    if (!isContentReport(reportType.value)) return false;
    return reportData.value?.content?.length === 0;
  });

  // Helper to check if report data is empty based on data structure type
  function checkReportHasNoData(dataChecker) {
    // Ads reports have a different data structure
    if (isAdsReportType.value) {
      const adsReportData = Object.values(reportData.value).filter((accountData) => {
        const channelData =
          accountData.metrics?.[reportMetric.value]?.[firstChannel.value] ??
          accountData.metrics?.[reportMetric.value] ??
          {};
        return dataChecker(channelData);
      });
      return adsReportData.length <= 0;
    }

    // Listening and Community reports with aggregation
    if (
      (isCommunityReportCategory.value || isListeningReportCategory.value) &&
      aggregateBy?.value !== LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL.value
    ) {
      const dataObjects = Object.entries(reportData.value).filter(([key, group]) => {
        const lowerChannelKey = group.data_type === 'CHANNEL' ? key : ALL_CHANNELS_KEY;
        const dataValues = group.metrics?.[reportMetric.value]?.[lowerChannelKey] ?? {};
        return dataChecker(dataValues);
      });
      return !dataObjects.length;
    }

    // Check root-level metrics data
    const channelData = reportData.value?.metrics?.[reportMetric.value]?.[firstChannel.value] ?? {};
    const allChannelsData =
      reportData.value?.metrics?.[reportMetric.value]?.[ALL_CHANNELS_KEY] ?? {};

    if (dataChecker(channelData) || dataChecker(allChannelsData)) return false;

    // Check account-level metrics data
    return Object.values(reportData.value).every((accountData) => {
      if (!accountData || isEmpty(accountData) || accountData?.user_has_access === false) {
        return true;
      }
      return metrics.value.every((metric) => {
        const channelMetrics = accountData.metrics?.[metric]?.[firstChannel.value] ?? {};
        const allChannelsMetrics = accountData.metrics?.[metric]?.[ALL_CHANNELS_KEY] ?? {};
        // When aggregate_by is BRAND, timeseries data is flat at metrics[metric] without a channel key
        const brandAggregatedMetrics =
          aggregateBy?.value === AGGREGATE_REPORT_TYPES.BRAND.value
            ? (accountData.metrics?.[metric] ?? {})
            : {};

        return (
          !dataChecker(channelMetrics) &&
          !dataChecker(allChannelsMetrics) &&
          !dataChecker(brandAggregatedMetrics)
        );
      });
    });
  }

  const timeSeriesReportHasNoData = computed(() => {
    if (!isTimeSeriesReport.value) return false;

    const hasTimeSeriesData = (data) =>
      !isEmpty(data) && Object.values(data).some((value) => negate(isNil)(value));

    return checkReportHasNoData(hasTimeSeriesData);
  });

  const metricReportHasNoData = computed(() => {
    if (isContentReport(reportType.value)) return false;
    if (isTimeSeriesReport.value) return false;

    const hasMetricData = (data) => !isEmpty(data) && (data.value != null || data.context != null);

    return checkReportHasNoData(hasMetricData);
  });

  const secondaryAggregateHasNoData = computed(() => {
    if (!secondaryAggregateBy.value) return false;
    return !Object.values(reportData.value).some((primaryData) => primaryData?.breakdowns);
  });

  const hasNoData = computed(() => {
    const reportDataObj = report.value?.data;
    if (report.value?.type === REPORTS.HEADER.value) return false;
    if (!reportDataObj && !reportData.value) return true;
    if (reportDataObj && isEmpty(Object.keys(reportDataObj))) return true;
    if (!reportData.value) return false;
    return (
      contentReportHasNoData.value ||
      timeSeriesReportHasNoData.value ||
      metricReportHasNoData.value ||
      secondaryAggregateHasNoData.value
    );
  });

  const reportIsInvalid = computed(() => {
    const bypassConnectionCheck = flagStore?.flags?.bypassConnectionCheck;
    const noPlatformConnections = isEmpty(platformStore.platformConnectionsMap);
    const noReportBrands = !reportBrands.value || reportBrands.value.length === 0;

    if (bypassConnectionCheck || noPlatformConnections || noReportBrands) return false;
    return allBrandsHaveChannelsDisconnected.value && hasNoData.value;
  });

  function canAccessData(id) {
    return reportData.value?.[id]?.user_has_access === true;
  }

  const noAccessToBrand = computed(() => {
    if (isCreatorReport.value) {
      return reportCreators.value.some((creator) => !canAccessData(parseCreatorId(creator.id)));
    }
    if (!reportBrands.value || reportBrands.value.length === 0) return false;
    if (isAdsReportType.value) {
      return reportBrands.value.some((brand) => brandsWithoutAdsPermission(brand));
    }

    return reportBrands.value.some((brand) => !canAccessData(brand?.id || brand));
  });

  const noAccessToBenchmarkReport = computed(() => {
    if (!isBenchmarkReport.value) return false;

    return !canAccessIndustryBenchmarks.value;
  });

  const brandsWithoutMetricAccess = computed(() => {
    const brands = reportBrands.value;

    if (!brands || brands.length === 0) return {};

    return brands.filter((brand) => {
      if (brand.isCompetitor) return true;

      if (reportMetricDetails.value.displayName) {
        const someBrandCanAccessMetric = identityStore.checkIfBrandPermissionsExist(
          reportMetricDetails.value?.permissions,
          [brand],
        );
        const requiredFeatureFlag = reportMetricDetails.value?.requiredFeatureFlag;
        const hasRequiredFlag = Boolean(
          !requiredFeatureFlag || (flagStore.ready && flagStore.flags?.[requiredFeatureFlag]),
        );
        return !hasRequiredFlag || !someBrandCanAccessMetric;
      }

      if (!reportChannels.value[0] || !reportMetric.value) return false;
      return canAccessMetrics(brand);
    });
  });

  const hasMetricAccessErrors = computed(() => {
    if (!brandsWithoutMetricAccess.value) return false;

    return Object.keys(brandsWithoutMetricAccess.value).length > 0;
  });

  const noAccessToMetric = computed(() => {
    const brands = reportBrands.value;
    if (!brands) return false;

    const nonCompetitorBrands = brands.filter((brand) => !brand.isCompetitor);
    if (nonCompetitorBrands.length === 0) return false;

    return nonCompetitorBrands.every((brand) => brandsWithoutMetricAccess.value[brand.id]);
  });

  const noAccessToCustomMetric = computed(() => {
    if (!isCustomMetricReportTile.value) return false;

    return isCustomMetricDeleted.value;
  });

  const accountDisconnectedError = computed(() => {
    if (
      !reportIsInvalid.value ||
      disconnectedChannel.value ||
      (reportChannels.value && reportChannels.value.length > 0)
    )
      return false;
    return true;
  });

  const errorTitle = computed(() => {
    if (
      noAccessToBrand.value ||
      noAccessToMetric.value ||
      noAccessToBenchmarkReport.value ||
      (isCompetitiveReport.value && noAccessToBrand.value)
    ) {
      return REPORT_ERROR_MESSAGES.NO_ACCESS_ERROR_TITLE;
    }

    if (reportIsInvalid.value) return REPORT_ERROR_MESSAGES.ACCOUNT_DISCONNECTED;
    if (noAccessToCustomMetric.value) return DASHBOARD_TOOLTIPS.CUSTOM_METRIC_NOT_FOUND;
    if (accountDisconnectedError.value) return '';
    if (hasNoData.value) return REPORT_ERROR_MESSAGES.NO_DATA_TITLE;

    return '';
  });

  const errorDescription = computed(() => {
    if (noAccessToBrand.value)
      return `${NO_ACCESS_MESSAGES.brand}<br />  Please contact the account administrator.`;
    if (noAccessToMetric.value)
      return `${NO_ACCESS_MESSAGES.metric}<br />  Please contact the account administrator.`;
    if (noAccessToBenchmarkReport.value) return NO_ACCESS_MESSAGES.feature;
    if (isCompetitiveReport.value && noAccessToBrand.value) return NO_ACCESS_MESSAGES.competitor;

    if (reportIsInvalid.value) {
      if (allBrandsHaveChannelsDisconnected.value)
        return REPORT_ERROR_MESSAGES.ACCOUNT_DISCONNECTED;
      if (disconnectedChannel.value)
        return getAccountDisconnectionMessage(disconnectedChannel.value);
    }

    if (accountDisconnectedError.value) return toolTips.accountDisconnection.all;
    if (hasNoData.value) return REPORT_ERROR_MESSAGES.NO_DATA_AVAILABLE;

    return '';
  });

  const hasError = computed(() => {
    if (loading.value) return false;

    return (
      noAccessToBrand.value ||
      noAccessToMetric.value ||
      noAccessToBenchmarkReport.value ||
      reportIsInvalid.value ||
      noAccessToCustomMetric.value ||
      accountDisconnectedError.value ||
      externalApiError.value ||
      reportRequiresAdAccountButDisconnected.value
    );
  });

  const hasErrors = computed(
    () =>
      hasChannelErrors.value ||
      hasCustomMetricErrors.value ||
      hasContentTagErrors.value ||
      hasMetricAccessErrors.value ||
      hasCompetitiveAccessErrors.value ||
      hasCompetitorAccessErrors.value ||
      hasAdsAccessErrors.value ||
      hasBenchmarkAccessErrors.value ||
      hasCommunityAccessErrors.value ||
      hasError.value,
  );

  const errorMessageForDisplay = computed(() => {
    if (noAccessToCustomMetric.value) return REPORT_ERROR_MESSAGES.CUSTOM_METRIC_NOT_FOUND;
    if (invalidTimeScaleForNonAdditiveMetric.value)
      return REPORT_ERROR_MESSAGES.INVALID_TIMESCALE_NON_ADDITIVE_METRIC;
    if (noAccessToBrand.value) return REPORT_ERROR_MESSAGES.NO_BRAND_ACCESS;
    if (noAccessToMetric.value) return REPORT_ERROR_MESSAGES.NO_METRIC_ACCESS;
    if (noAccessToBenchmarkReport.value) return REPORT_ERROR_MESSAGES.NO_BENCHMARK_ACCESS;
    if (accountDisconnectedError.value && reportRequiresAdAccountButDisconnected.value) {
      return REPORT_ERROR_MESSAGES.AD_ACCOUNT_AND_PLATFORM_DISCONNECTED;
    }
    if (reportIsInvalid.value || accountDisconnectedError.value)
      return REPORT_ERROR_MESSAGES.ALL_BRANDS_DISCONNECTED;
    if (reportRequiresAdAccountButDisconnected.value)
      return reportIsOrganicAndPromotedWithDisconnectedAdAccount.value
        ? REPORT_ERROR_MESSAGES.ORGANIC_AND_PROMOTED_AD_ACCOUNT_DISCONNECTED
        : REPORT_ERROR_MESSAGES.AD_ACCOUNT_DISCONNECTED;
    if (externalApiError.value) {
      return REPORT_ERROR_MESSAGES.SERVER_ERROR;
    }
    if (noAccessToTopics.value) return REPORT_ERROR_MESSAGES.NO_ACCESS_TO_TOPICS;
    if (crossOrgTopics.value) return REPORT_ERROR_MESSAGES.CROSS_ORG_TOPICS;

    return REPORT_ERROR_MESSAGES.NO_DATA_AVAILABLE;
  });

  const showErrorState = computed(
    () =>
      serverErrorMessage.value ||
      (!loading.value &&
        (hasNoData.value ||
          externalApiError.value ||
          reportRequiresAdAccountButDisconnected.value) &&
        (noAccessToBrand.value ||
          noAccessToMetric.value ||
          noAccessToBenchmarkReport.value ||
          reportIsInvalid.value ||
          noAccessToCustomMetric.value ||
          accountDisconnectedError.value ||
          noAccessToTopics.value ||
          crossOrgTopics.value ||
          reportRequiresAdAccountButDisconnected.value)),
  );

  return {
    hasNoData,
    errorTitle,
    errorDescription,
    errorMessageForDisplay,
    showErrorState,
    hasErrors,
    externalApiError,
    disconnectedChannels,
    brandsUserCannotAccess,
    brandsWithoutMetricAccess,
    brandsWithoutContentTagAccess,
    brandsWithoutCustomMetricAccess,
    brandsWithoutCompetitiveAccess,
    competitorsWithoutAccess,
    creatorsWithoutAccess,
    hasCompetitorAccessErrors,
    brandsWithoutDashboardsAccess,
    brandsWithoutAdsAccess,
    brandsWithoutBenchmarkAccess,
    brandsWithoutCommunityAccess,
    hasCommunityAccessErrors,
    getChannelIconName,
    canAccessCompetitor,
    userOrBrandCanAccessCompetitor,
    brandCanAccessBenchmark,
    brandCanAccessAds,
    userCanAccessAds,
    canAccessAdsForBrand,
    hasAdsAccessErrors,
    brandCanAccessCommunity,
    reportRequiresAdAccountButDisconnected,
    reportIsOrganicAndPromotedWithDisconnectedAdAccount,
    adAccountDisconnectedPlatform,
    brandsWithoutRequiredAdAccount,
    getConnectionUrl,
  };
}
